import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { Router } from '@angular/router';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, finalize, type Observable, of, pipe, switchMap } from 'rxjs';

import { environment } from '@env';

import { AppPath, type Preferences, type User, UserType } from '@kernel';

import {
  LOCAL_STORAGE,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE,
  type StorageChannelMessage,
} from '@core/storage';

import { AuthService } from './auth.service';

interface AuthState {
  readonly initialized: boolean;
  readonly loading: boolean;
  readonly user: User | null;
  readonly submitting: boolean;
  readonly error: string | null;
}

export const AuthStore = signalStore(
  { providedIn: 'root' },

  environment.withDevtools('auth'),

  withState<AuthState>({
    initialized: false,
    loading: false,
    user: null,
    submitting: false,
    error: null,
  }),

  withComputed(({ user }) => ({
    isAuthenticated: computed(() => user() !== null),
    isSuperAdmin: computed(() => user()?.userType === UserType.SuperAdmin),
    isAdmin: computed(() => user()?.userType === UserType.Admin),
    isRegularUser: computed(() => user()?.userType === UserType.User),
  })),

  withMethods((store) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const local = inject(LOCAL_STORAGE);
    const session = inject(SESSION_STORAGE);

    const errorCodesMap: Record<string, string> = {
      INVALID_CREDENTIALS: 'common.error.invalidCredentials',
      NOT_ACTIVE: 'common.error.userNotActive',
      NO_ROLE: 'common.error.userHasNoRole',
    };

    const setUser = (user: User | null, broadcast = true): void => {
      patchState(store, { user });

      if (broadcast) channel.postMessage(user);
    };

    // Mirrors a user update from another tab; clears this tab's session too on logout
    // so it doesn't linger. Never re-broadcasts (would loop back through the channel).
    const handleCrossTabUser = (user: User | null): void => {
      setUser(user, false);

      if (user === null) session.clear();
    };

    const channel = local.openChannel<User | null>(
      LOCAL_STORAGE_KEYS.auth.user,
      handleCrossTabUser,
    );

    const clearAuth = (): void => {
      setUser(null);
      local.remove(LOCAL_STORAGE_KEYS.auth.user);
      session.clear();
    };

    const cleanup = (): void => {
      channel.close();
    };

    const authErrorKey = (error: unknown): string => {
      const code = ((error as HttpErrorResponse)?.error as { code: string }).code;
      return errorCodesMap[code] ?? 'common.error.serverFailed';
    };

    const handleLogin = (request$: Observable<User>): Observable<User> =>
      request$.pipe(
        tapResponse({
          next: (user) => {
            setUser(user);
          },
          error: (error: unknown) => {
            patchState(store, { error: authErrorKey(error) });
          },
        }),
        finalize(() => patchState(store, { submitting: false })),
      );

    return {
      clearAuth,

      clearLoginErrors: (): void => {
        patchState(store, { error: null });
      },

      validateEmailInSso: rxMethod<{ email: string; onNotLinked: () => void }>(
        pipe(
          exhaustMap(({ email, onNotLinked }) => {
            patchState(store, { submitting: true, error: null });

            return authService.validateEmailInSso(email).pipe(
              tapResponse({
                next: (response) => {
                  if (response.status === 'NOT_LINKED') {
                    onNotLinked();
                    return;
                  }

                  if (response.status === 'LINKED' && response.authUrl) {
                    if (typeof window === 'undefined') return;
                    window.location.assign(response.authUrl);
                  }
                },
                error: () => patchState(store, { error: 'common.error.serverFailed' }),
              }),
              finalize(() => patchState(store, { submitting: false })),
            );
          }),
        ),
      ),

      loginWithoutSso: rxMethod<{ username: string; password: string }>(
        pipe(
          exhaustMap((request) => {
            patchState(store, { submitting: true, error: null });

            return handleLogin(authService.loginWithoutSso(request));
          }),
        ),
      ),

      loginWithSso: rxMethod<{ code: string; state: string }>(
        pipe(
          exhaustMap((request) => {
            patchState(store, { submitting: true, error: null });

            return handleLogin(authService.loginWithSso(request));
          }),
        ),
      ),

      setPreferences: (preferences: Preferences): void => {
        const user = store.user();

        if (!user) return;

        setUser({ ...user, preferences });
      },

      hasRole: (role: UserType): boolean => store.user()?.userType === role,
      hasPermission: (permission: string): boolean =>
        store.user()?.permissions?.includes(permission) ?? false,

      initialize: (): Observable<User | null> => {
        const stored =
          local.getJSON<StorageChannelMessage<User | null>>(LOCAL_STORAGE_KEYS.auth.user)
            ?.payload ?? null;

        if (stored) setUser(stored, false);

        patchState(store, { initialized: true });

        return of(stored);
      },

      logout: rxMethod<void>(
        pipe(
          switchMap(() => {
            patchState(store, { loading: true });

            return authService.logout().pipe(
              finalize(() => {
                clearAuth();
                patchState(store, { loading: false });
                void router.navigateByUrl(AppPath.Login);
              }),
            );
          }),
        ),
      ),

      _setUser: setUser,
      _cleanup: cleanup,
    };
  }),

  withHooks({
    onInit(store): void {
      const local = inject(LOCAL_STORAGE);
      const stored = local.getJSON<StorageChannelMessage<User | null>>(
        LOCAL_STORAGE_KEYS.auth.user,
      );

      store._setUser(stored?.payload ?? null, false);
    },

    onDestroy(store): void {
      store._cleanup();
    },
  }),
);
